import type { Decimal } from '@snap-types/types/decimal';

import {
  SnapRoundingDecimalPlaces,
  SnapRoundingMethod,
  type SnapRoundingOptions,
} from './types';

export * from './types';

export function snapRound(
  value: Decimal,
  options: SnapRoundingOptions,
): Decimal {
  const { method } = options;

  switch (method) {
    case SnapRoundingMethod.None:
      return value;

    case SnapRoundingMethod.Percent:
      return value.toDecimalPlaces(SnapRoundingDecimalPlaces.Percent);

    case SnapRoundingMethod.DecimalPercent:
      return value.toDecimalPlaces(SnapRoundingDecimalPlaces.DecimalPercent);

    case SnapRoundingMethod.Decimal:
      return value.toDecimalPlaces(SnapRoundingDecimalPlaces.Decimal);

    case SnapRoundingMethod.Custom: {
      const { decimalPlaces } = options;

      return value.toDecimalPlaces(decimalPlaces);
    }

    /* v8 ignore next 4 lines */
    default: {
      const exhaustive: never = method;
      throw new Error(`Unhandled rounding method: ${exhaustive}`);
    }
  }
}

/**
 * Rounds a value expressed *per each* — a unit price, a unit cost, a per-piece
 * rate — rather than an absolute amount.
 *
 * Identical to {@link snapRound} for every method except `Custom`, which is the
 * only one carrying a site-configured precision and therefore the only one that
 * has to follow the selling unit of measure.
 */
export function snapRoundPerUnit(
  value: Decimal,
  options: SnapRoundingOptions,
): Decimal {
  if (options.method !== SnapRoundingMethod.Custom) {
    return snapRound(value, options);
  }

  return value.toDecimalPlaces(options.perUnitDecimalPlaces);
}

export function snapRoundPercent(value: Decimal) {
  return snapRound(value, { method: SnapRoundingMethod.Percent });
}

export function snapRoundDecimalPercent(value: Decimal) {
  return snapRound(value, { method: SnapRoundingMethod.DecimalPercent });
}

export function snapRoundDecimal(value: Decimal) {
  return snapRound(value, { method: SnapRoundingMethod.Decimal });
}

/**
 * Rounds to a precision with no unit interpretation attached — the caller is
 * asserting that the value and `decimalPlaces` already share a basis. Quantity
 * entry is the case that wants this: it rounds what the user typed, in the unit
 * they typed it in, before any conversion.
 *
 * Anything rounding a *stored* per-unit value wants {@link snapRoundPerUnit}
 * with {@link SnapRoundingOptionsCustom} instead.
 */
export function snapRoundCustom(value: Decimal, decimalPlaces: number) {
  return value.toDecimalPlaces(decimalPlaces);
}
